import core from '@gin-betting/core';

import { DEFAULT_STREAM_URL } from 'app-constants';
import localeManager from 'services/locale-manager';
import {
    BOTTOM_MENU_HEIGHT,
    COMPACT_BASE_HEIGHT,
    COMPACT_BASE_WIDTH,
    DOUBLE_BASE_HEIGHT,
    DOUBLE_BASE_WIDTH,
    MATCH_TRACKER_CONTAINER,
    MIN_WIDTH_FOR_DOUBLE_MODE,
    MOMENTUM_HEIGHT,
    PlayerType,
    SCOREBOARD_HEIGHT,
    WIDGET_LOADER,
} from './constants';
import {
    GetCurrentBetradarSizeInput,
    GetCurrentBetradarSizeOutput,
} from './types';

const {
    utils: { getBettingOptions },
} = core;

export function getPlayerType(url: string): PlayerType {
    const lUrl = url.toLocaleLowerCase();

    switch (true) {
        case lUrl.includes('.swf') && lUrl.includes('.douyu'): {
            return PlayerType.DOUYU;
        }

        case lUrl.includes('.swf'): {
            return PlayerType.SWF;
        }

        case lUrl.includes('twitch'): {
            return PlayerType.TWITCH;
        }

        case lUrl.includes('youtube'): {
            return PlayerType.YOUTUBE;
        }

        default: {
            return PlayerType.IFRAME;
        }
    }
}

export function isStreamUrlValid(streamUrl: string): boolean {
    return streamUrl !== '' && streamUrl !== DEFAULT_STREAM_URL;
}

export const loadMatchTracker = (): HTMLScriptElement => {
    const { locale } = getBettingOptions();

    const script = document.createElement('script');
    script.setAttribute('src', WIDGET_LOADER);
    script.setAttribute('n', 'SIR');
    script.setAttribute(
        'data-sr-language',
        localeManager.convert(locale, { from: 'cms', to: 'matchTracker' })
    );
    script.async = true;

    document.head.appendChild(script);

    return script;
};

export const initMatchTracker = ({
    betradarId,
    layout,
    hasScoreboard,
}: {
    betradarId: string | null;
    layout: 'single' | 'double';
    hasScoreboard: boolean;
}): void => {
    if (window.SIR && betradarId) {
        window.SIR('addWidget', MATCH_TRACKER_CONTAINER, 'match.lmtPlus', {
            pitchBgImage: '',
            showOdds: false,
            layout,
            scoreboard: hasScoreboard ? 'compact' : 'disable',
            momentum: hasScoreboard ? 'compact' : 'disable',
            matchId: betradarId,
        });
    }
};

export const removeMatchTracker = (): void => {
    if (window.SIR) {
        window.SIR('removeWidget', MATCH_TRACKER_CONTAINER);
    }
};

const getCompactModeWithScoreboardAndMomentum = (
    clientWidth: number,
    clientHeight: number
): [number, string | number] => {
    const maxWidth =
        ((clientHeight -
            BOTTOM_MENU_HEIGHT -
            SCOREBOARD_HEIGHT -
            MOMENTUM_HEIGHT) /
            COMPACT_BASE_HEIGHT) *
        COMPACT_BASE_WIDTH;

    if (maxWidth < clientWidth) {
        return [maxWidth, clientHeight];
    }

    return [clientWidth, 'auto'];
};

const getFullWidthNoPanels = (
    clientWidth: number
): [number, string | number] => {
    const maxHeight =
        (clientWidth / COMPACT_BASE_WIDTH) * COMPACT_BASE_HEIGHT +
        BOTTOM_MENU_HEIGHT;

    return [clientWidth, maxHeight];
};

const getCompactModeWithoutPanels = (
    clientWidth: number,
    clientHeight: number
): [number, number | string] => {
    const maxWidth =
        ((clientHeight - BOTTOM_MENU_HEIGHT) / COMPACT_BASE_HEIGHT) *
        COMPACT_BASE_WIDTH;

    if (maxWidth < clientWidth) {
        return [maxWidth, clientHeight];
    }

    const maxHeight = (clientWidth / COMPACT_BASE_WIDTH) * COMPACT_BASE_HEIGHT;

    const resultingHeight = maxHeight - BOTTOM_MENU_HEIGHT;

    return [clientWidth, resultingHeight];
};

const getDoubleModeWithPanels = (
    clientWidth: number
): [number, number | string] => {
    const maxHeight =
        (clientWidth / DOUBLE_BASE_WIDTH) * DOUBLE_BASE_HEIGHT +
        BOTTOM_MENU_HEIGHT +
        MOMENTUM_HEIGHT +
        SCOREBOARD_HEIGHT;

    return [clientWidth, maxHeight];
};

export const getCurrentBetradarSize = ({
    clientWidth,
    clientHeight,
    isSmall,
    isFullscreenActive,
    isListPage,
}: GetCurrentBetradarSizeInput): GetCurrentBetradarSizeOutput => {
    if (isFullscreenActive) {
        return getCompactModeWithScoreboardAndMomentum(
            clientWidth,
            clientHeight
        );
    }

    if (isSmall) {
        return getFullWidthNoPanels(clientWidth);
    }

    if (clientWidth < MIN_WIDTH_FOR_DOUBLE_MODE) {
        return getCompactModeWithoutPanels(clientWidth, clientHeight);
    }

    if (isListPage) {
        return getDoubleModeWithPanels(clientWidth);
    }

    return getCompactModeWithoutPanels(clientWidth, clientHeight);
};
